import type { IFormGenContext, IFormGenSchema } from "@artifact-management/json-dynamic-form";
import { MultiItemFormStep } from "@complex/MultiItemNavigation/FormStep";
import { MultiItemNavigationFooter } from "@complex/MultiItemNavigation/footer";
import { MultiItemNavigationHeader } from "@complex/MultiItemNavigation/header";
import { useMultiItemForm } from "@complex/MultiItemNavigation/logics";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import {
  Button,
  CircularProgress,
  Collapse,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Dialog } from "@surfaces/Dialog";
import { DialogFooter } from "@surfaces/DialogFooter";
import { useEffect, useState } from "react";
import type { FieldValues } from "react-hook-form";
import { GenerationLoading } from "./GenerationLoading";

const EMPTY_ITEMS: never[] = [];

export interface ICreateWithAIModalLabels {
  title: string;
  instruction: string;
  generate: string;
  confirm: string;
  cancel?: string;
  items?: string;
  generating?: string;
  empty?: string;
  failed?: (failedCount: number, total: number) => string;
}

export interface ICreateWithAIGeneration<T> {
  items?: T[];
  running?: boolean;
  count?: number;
  total?: number;
  error?: boolean;
}

export interface ICreateWithAIModalProps<TRaw extends FieldValues = FieldValues, TForm extends FieldValues = TRaw> {
  open: boolean;
  labels: ICreateWithAIModalLabels;
  generation?: ICreateWithAIGeneration<TRaw>;
  schema?: IFormGenSchema;
  context?: IFormGenContext;
  onClose: () => void;
  onGenerate: (text: string) => void;
  // biome-ignore lint/suspicious/noConfusingVoidType: `void` garde assignables les callbacks sans valeur de retour
  onConfirm: (items: TForm[]) => void | number[] | Promise<void | number[]>;
  loading?: boolean;
}

export const CreateWithAIModal = <TRaw extends FieldValues = FieldValues, TForm extends FieldValues = TRaw>({
  open,
  labels,
  generation,
  schema,
  context,
  onClose,
  onGenerate,
  onConfirm,
  loading = false,
}: ICreateWithAIModalProps<TRaw, TForm>) => {
  const {
    title,
    instruction,
    generate: generateLabel,
    confirm: confirmLabel,
    cancel: cancelLabel = "Annuler",
    items: itemsTitle = "Éléments générés",
    generating: generatingLabel = "En cours de génération",
    empty: emptyLabel = "Aucun élément n'a été détecté dans ce texte.",
    failed: failedLabel = (failedCount, total) => `Échec de génération pour ${failedCount} élément(s) sur ${total}.`,
  } = labels;
  const {
    items = EMPTY_ITEMS,
    running: generating = false,
    count: generatedCount,
    total: generatedTotal,
    error = false,
  } = generation ?? {};

  const [text, setText] = useState("");
  const [sectionOpen, setSectionOpen] = useState(true);
  const [hasGenerated, setHasGenerated] = useState(false);

  const isEditing = Boolean(schema) && items.length > 0 && !generating;

  const detectedTotal = generatedTotal ?? 0;
  const failedCount = Math.max(0, detectedTotal - items.length);
  const settled = hasGenerated && !generating && !error;
  const showEmpty = settled && detectedTotal === 0;
  const showFailed = settled && failedCount > 0;

  const {
    currentItem,
    currentKey,
    currentIndex,
    totalCount,
    isFirst,
    isLast,
    deleteCurrentItem,
    stepRef,
    handleNext,
    handlePrevious,
    handleConfirm,
  } = useMultiItemForm<TRaw, TForm>({ items, onConfirm });

  useEffect(() => {
    if (generating || isEditing) {
      setSectionOpen(false);
    }
  }, [generating, isEditing]);

  useEffect(() => {
    if (!open) {
      setText("");
      setSectionOpen(true);
      setHasGenerated(false);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleDelete = () => {
    if (totalCount <= 1) {
      handleClose();
      return;
    }
    deleteCurrentItem();
  };

  const hasText = text.trim().length > 0;
  const disabled = generating || items.length > 0;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h4">{title}</Typography>
          <IconButton onClick={handleClose} sx={{ mr: -1 }}>
            <CloseIcon sx={(theme) => ({ color: theme.palette.black.light })} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          onClick={() => setSectionOpen((prev) => !prev)}
          sx={(theme) => ({
            alignItems: "center",
            justifyContent: "space-between",
            pt: 0,
            pb: 2,
            cursor: "pointer",
            color: theme.palette.primary.main,
          })}
        >
          <Typography variant="subtitle1SemiBold" color="primary">
            Texte utilisé pour compléter les champs
          </Typography>
          {sectionOpen ? <ExpandLessIcon color="primary" /> : <ExpandMoreIcon color="primary" />}
        </Stack>
        <Collapse in={sectionOpen}>
          <Stack sx={{ gap: 2 }}>
            <Typography variant="body1">{instruction}</Typography>
            <TextField
              multiline
              minRows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={disabled}
              fullWidth
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={generating ? <CircularProgress size={14} /> : <LightbulbOutlined />}
              disabled={!hasText || disabled}
              onClick={() => {
                setHasGenerated(true);
                onGenerate(text);
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              {generateLabel}
            </Button>
            {error && (
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
                Une erreur est survenue lors de la génération. Veuillez réessayer.
              </Typography>
            )}
          </Stack>
        </Collapse>

        {generating && <GenerationLoading label={generatingLabel} count={generatedCount} total={generatedTotal} />}

        {showEmpty && (
          <Typography variant="body1" sx={{ mt: 2 }}>
            {emptyLabel}
          </Typography>
        )}

        {showFailed && (
          <Typography variant="body2" sx={(theme) => ({ mt: 2, color: theme.palette.warning.dark })}>
            {failedLabel(failedCount, detectedTotal)}
          </Typography>
        )}

        {isEditing && schema && currentItem && (
          <Stack sx={{ gap: 2, mt: 2 }}>
            <MultiItemNavigationHeader
              title={itemsTitle}
              currentIndex={currentIndex}
              totalCount={totalCount}
              onDelete={handleDelete}
            />

            <MultiItemFormStep key={currentKey} ref={stepRef} item={currentItem} schema={schema} context={context} />
          </Stack>
        )}
      </DialogContent>

      {isEditing ? (
        <MultiItemNavigationFooter
          isFirst={isFirst}
          isLast={isLast}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          onCancel={handleClose}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onConfirm={handleConfirm}
          disabled={loading}
        />
      ) : (
        <DialogFooter
          actions={[
            { label: cancelLabel, startIcon: <CloseIcon />, onClick: handleClose },
            {
              label: confirmLabel,
              startIcon: <CheckCircleOutlined />,
              variant: "contained",
              disabled: true,
              onClick: () => {},
            },
          ]}
        />
      )}
    </Dialog>
  );
};
